/**
 * As etapas do funil — configurar o que o quadro mostra.
 *
 * ── Por que isto existe ───────────────────────────────────────────────────
 *
 * A A01 §4 mediu o funil que vem no motor de CRM — `NEW`, `SCREENING`,
 * `MEETING`, `PROPOSAL`, `CUSTOMER` — e escreveu a conclusão: *"o funil padrão
 * do Twenty não é o funil de uma revenda de veículos"*. Sem esta configuração,
 * toda revenda trabalha no funil de outra pessoa.
 *
 * ── `valor` é imutável, `rotulo` é o que muda ─────────────────────────────
 *
 * O `valor` é o que está gravado em cada oportunidade. Trocá-lo deixaria todas
 * as que estivessem naquela etapa apontando para algo que não existe mais — e
 * elas sumiriam do quadro, sem erro nenhum.
 *
 * Então o contrato separa os dois: quem nasce escolhe o valor uma vez, e
 * depois só o rótulo e a cor mudam. É a diferença entre renomear uma coluna e
 * apagar os negócios dela.
 */

import { z } from 'zod';

/**
 * As cores que o motor de CRM aceita num `SELECT`.
 *
 * Lista fechada, e não hexadecimal como nas etiquetas: aqui quem guarda é o
 * Twenty, e ele só conhece estes nomes. Mandar `#1F93FF` seria recusado — ou,
 * pior, aceito e ignorado.
 */
export const CORES_DE_ETAPA = [
  'green',
  'turquoise',
  'sky',
  'blue',
  'purple',
  'pink',
  'red',
  'orange',
  'yellow',
  'gray',
] as const;

/** Ganho ou perdido: o resultado de uma etapa final (057). */
export const ResultadoDaEtapaSchema = z.enum(['ganho', 'perdido']);

export type ResultadoDaEtapa = z.infer<typeof ResultadoDaEtapaSchema>;

export const EtapaDoFunilSchema = z
  .object({
    /** O que está gravado na oportunidade. Nunca muda depois de criada. */
    valor: z.string().min(1),
    /**
     * De que funil esta etapa é.
     *
     * Derivado do `valor` — `F_VENDAS_NOVO` é do funil `VENDAS`, e etapa sem
     * separador é do `padrao`. Vem PRONTO porque a derivação mora no
     * comercial, que a interface não importa (§17.1): sem este campo, a tela
     * de Configurações teria de reescrever a convenção do separador, e o dia em
     * que ela mudasse existiriam duas regras — uma delas errada.
     */
    funil: z.string().min(1),
    /** O que a pessoa lê no quadro. */
    rotulo: z.string().min(1),
    cor: z.enum(CORES_DE_ETAPA),
    posicao: z.number().int().nonnegative(),
    /**
     * A etapa é de trabalho — dá para tirar o negócio dela arrastando.
     *
     * `false` é etapa FINAL: ganho, perdido, o que encerra. Dela não se arrasta
     * para fora, porque reabrir um negócio é decisão, não gesto.
     *
     * ── Por que isto passou a ser campo da etapa ──────────────────────────
     *
     * Era só a lista `etapas_abertas` da regra do tenant, escrita no
     * provisionamento e nunca mais. Como é lista de PERMISSÃO, toda etapa nova
     * caía fora dela e nascia final: criava-se "Test drive", o negócio entrava
     * e não saía mais.
     *
     * A lista continua sendo a fonte — dizê-lo em dois lugares é como as duas
     * respostas divergem. O que mudou é que a etapa carrega a resposta até
     * quem precisa dela, e criar uma etapa passa a manter a lista.
     */
    aberta: z.boolean(),
    /**
     * O que a etapa FINAL significa: a venda ganha ou a perdida (057).
     *
     * `null` em toda etapa aberta, e na final que existia antes da 057 e
     * ninguém classificou. Não se deduz do nome: "Cliente" pode ser ganho numa
     * revenda e só uma etapa de cadastro em outra.
     */
    resultado: ResultadoDaEtapaSchema.nullable(),
  })
  .strict();

export type EtapaDoFunil = z.infer<typeof EtapaDoFunilSchema>;

export const EtapasDoFunilSchema = z
  .object({
    itens: z.array(EtapaDoFunilSchema),
  })
  .strict();

export const CriarEtapaSchema = z
  .object({
    /**
     * Nasce de trabalho, salvo dizer o contrário.
     *
     * Quem acrescenta uma etapa está quase sempre abrindo um passo no meio do
     * caminho. Nascer final é o que produziu o defeito — e o estrago é pior
     * nesse sentido: negócio que entra e não sai só aparece dias depois.
     */
    aberta: z.boolean().optional(),
    /*
     * MAIÚSCULAS com sublinhado — é a forma que o motor usa nas cinco que já
     * existem, e misturar formas faria o quadro ter `PROPOSAL` ao lado de
     * `test-drive`. O rótulo é onde a pessoa escreve como quiser.
     */
    valor: z
      .string()
      .trim()
      .min(2)
      .max(40)
      .regex(
        /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)*$/,
        'Use MAIÚSCULAS e sublinhado, como TEST_DRIVE — é a forma das etapas que já existem.',
      ),
    rotulo: z.string().trim().min(1).max(40),
    cor: z.enum(CORES_DE_ETAPA),
    /**
     * Em QUAL funil ela entra. Ausente é o `padrao`.
     *
     * O `valor` continua vindo sem prefixo, e quem compõe `F_VENDAS_NOVO` é o
     * servidor. É de propósito: a convenção do separador é do comercial, e
     * pedir o valor já prefixado faria cada cliente montá-lo por conta —
     * inclusive o que escrevesse `VENDAS_NOVO`, com um sublinhado só, que o
     * contrato aceita e que cairia calado no funil errado.
     */
    funil: z.string().trim().min(1).max(30).optional(),
  })
  .strict();

export const RenomearEtapaSchema = z
  .object({
    rotulo: z.string().trim().min(1).max(40).optional(),
    cor: z.enum(CORES_DE_ETAPA).optional(),
    aberta: z.boolean().optional(),
    /**
     * Ganho ou perdido. Dizer um resultado FECHA a etapa: só etapa final tem
     * resultado. `null` tira a classificação sem reabrir.
     */
    resultado: ResultadoDaEtapaSchema.nullable().optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.rotulo !== undefined ||
      v.cor !== undefined ||
      v.aberta !== undefined ||
      v.resultado !== undefined,
    { message: 'Diga o que mudar: o rótulo, a cor, se é final, o resultado, ou mais de um.' },
  )
  .refine((v) => !(v.aberta === true && v.resultado != null), {
    message: 'Etapa aberta não tem resultado: ganho e perdido são de etapa final.',
  });

export const ReordenarEtapasSchema = z
  .object({
    /**
     * A ordem desejada, pelos valores.
     *
     * Quem não for citado vai para o fim, na ordem em que estava — assim uma
     * lista incompleta reordena o que citou sem sumir com o resto, que seria
     * apagar etapa por omissão.
     */
    ordem: z.array(z.string().min(1)).min(1),
  })
  .strict();
